import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

// Material
import { makeStyles } from "@material-ui/core/styles";
import RadioGroup from "@material-ui/core/RadioGroup";

// Config
import { urls, getRoute } from "../../config/routes";
import { capturePages } from "../../config/values";

// Hooks
import { useAppInsights } from "../../utils/telemetry/AppInsights";
import useInjuries from "../../hooks/useInjuries";
import useTranslate from "../../hooks/useTranslate";

// Actions
import {
  updateInjury,
  uploadDescriptions,
  uploadTreatment
} from "../../state/injuries/actions";

// Components
import CategoryItem from "./CategoryItem";
import EditMediaForm from "../EditMediaForm";
import InteractiveBodyMap from "./InteractiveBodyMap";
import LoadingSection from "../LoadingSection";
import Text from "../Text";

const useStyles = makeStyles(theme => ({
  inputItem: {
    marginBottom: theme.spacing(4),
    display: "flex",
    flexDirection: "column"
  },
  headingWrapper: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2)
  }
}));

const categories = [
  "StabWound",
  "IncisedWound",
  "Laceration",
  "Bruising",
  "Swelling",
  "Burn",
  "Other"
];

const loadInitialValues = (
  { bodySection, category = categories[5].value, descriptions, treatments },
  isDraft
) => ({
  bodySection,
  category,
  descriptions: isDraft ? [descriptions[0] ? descriptions[0].text : ""] : [""],
  treatments: isDraft ? [treatments[0] ? treatments[0].text : ""] : [""]
});

const filterEmpty = arrayStrings => arrayStrings.filter(s => s && s !== "");
const toApiValue = value => (value.trim() === "" ? null : value);

export default () => {
  const { trackException } = useAppInsights();
  const classes = useStyles();
  const dispatch = useDispatch();
  const getText = useTranslate();

  const { caseId, injuryId } = useParams();
  const { data, imageHeaders, isDeletingImage, isLoading, loadingHeaders } =
    useInjuries(caseId, injuryId);

  const {
    data: { isDraft }
  } = useSelector(state => state.caseDetail);

  if (isLoading || !data) return <LoadingSection />;

  const onSubmit = (values, { setSubmitting, resetForm, goBack }) => {
    const treatments = filterEmpty(values.treatments);
    const descriptions = filterEmpty(values.descriptions);
    if (isDraft) {
      const currentDescription =
        (data.descriptions[0] && data.descriptions[0].text) || "";
      const currentTreatment =
        (data.treatments[0] && data.treatments[0].text) || "";
      const newDescription = values.descriptions[0] || "";
      const newTreatment = values.treatments[0] || "";

      // Skip the PUT if unchanged
      if (
        values.bodySection === data.bodySection &&
        values.category === data.category &&
        newDescription.trim() === currentDescription.trim() &&
        newTreatment.trim() === currentTreatment.trim()
      ) {
        setSubmitting(false);
        goBack();
        return;
      }

      dispatch(
        updateInjury({
          caseId,
          injuryId,
          form: {
            ...data,
            bodySection: values.bodySection,
            category: values.category,
            description: toApiValue(newDescription),
            treatment: toApiValue(newTreatment)
          },
          onFinish: success => {
            setSubmitting(false);
            if (success) goBack();
            else {
              trackException(
                `Failed to update injury: ${injuryId} at case: ${caseId}`
              );
            }
          }
        })
      );
    } else
      dispatch(
        uploadTreatment({
          caseId,
          injuryId,
          texts: treatments,
          onFinish: successTreatment => {
            if (descriptions.length > 0)
              dispatch(
                uploadDescriptions({
                  caseId,
                  injuryId,
                  texts: descriptions,
                  onFinish: successDescription => {
                    setSubmitting(false);
                    if (successDescription && successTreatment) goBack();
                    else {
                      trackException(
                        `Failed to add description or treatment to injury: ${injuryId} at case: ${caseId}`
                      );
                    }
                  }
                })
              );
            else {
              setSubmitting(false);
              resetForm();
              if (successTreatment) goBack();
              else {
                trackException(
                  `Failed to add treatment to injury: ${injuryId} at case: ${caseId}`
                );
              }
            }
          }
        })
      );
  };

  const getConstantValues = arr => (data && !isDraft ? data[arr] : []);

  const extraSection = ({ values, setValues }) => (
    <>
      <div className={classes.inputItem}>
        <div className={classes.headingWrapper}>
          <Text text="editInjuryPage.categoryTitle" type="subtitleBold" />
        </div>
        <RadioGroup
          aria-label="gender"
          name="category"
          onChange={({ target }) =>
            setValues({ ...values, category: target.value })
          }
        >
          {categories.map(key => (
            <CategoryItem
              category={key}
              checked={values.category.toLowerCase() === key.toLowerCase()}
              isDraft={isDraft}
              key={key}
            />
          ))}
        </RadioGroup>
      </div>

      <div className={classes.inputItem}>
        <div className={classes.headingWrapper}>
          <Text text="common.valueLabels.bodyMap" type="subtitleBold" />
        </div>
        <InteractiveBodyMap
          onChange={v => setValues({ ...values, bodySection: v })}
          value={values.bodySection}
        />
      </div>
    </>
  );

  return (
    <EditMediaForm
      caseId={caseId}
      itemId={injuryId}
      page={capturePages.INJURY}
      title={getText(`common.bodySection.${data.bodySection}`)}
      exitPromptText={getText("editInjuryPage.exitPrompt")}
      fields={[
        {
          name: "descriptions",
          title: getText("editInjuryPage.provideDescriptionTitle"),
          label: getText("editInjuryPage.descriptionLabel"),
          placeholder: getText("editInjuryPage.descriptionPlaceholder"),
          addButtonText: getText(
            "editInjuryPage.descriptionAddButton"
          ).toUpperCase(),
          constantValues: getConstantValues("descriptions")
        },
        {
          name: "treatments",
          title: getText("editInjuryPage.treatmentInputTitle"),
          label: getText("editInjuryPage.treatmentInputLabel"),
          placeholder: getText("editInjuryPage.treatmentInputPlaceholder"),
          addButtonText: getText(
            "editInjuryPage.treatmentInputAddButton"
          ).toUpperCase(),
          constantValues: getConstantValues("treatments")
        }
      ]}
      extraSection={extraSection}
      images={data.images}
      imageHeaders={imageHeaders}
      loadingHeaders={loadingHeaders}
      isDeletingImage={isDeletingImage}
      isDraft={isDraft}
      initialValues={loadInitialValues(data, isDraft)}
      onSubmit={onSubmit}
      detailRoute={getRoute(urls.injuryDetail, { caseId, injuryId })}
    />
  );
};
